import React from "react";
import { Box } from "@mui/material";
import { DotLegalMultiSelect, DotLegalTextField } from "@dotlegal/dotlegal-ui-components";
import DotLegalWarningTriangle from "@common/components/dotLegalWarningTriangle/DotLegalWarningTriangle.tsx";
import { useTranslation } from "@localization/useTranslation";
import FormulaEditor from "./FormulaEditor.tsx";
import { CustomCalculatedFieldProps, useCustomCalculatedField } from "./CustomCalculatedField.hooks.ts";

function CustomCalculatedField(props: CustomCalculatedFieldProps) {
    const { translateString } = useTranslation();
    const {
        tokens,
        isFormulaEditorEnabled,
        amountFieldOptions,
        includedAmountFieldIds,
        onIncludedAmountFieldsChange,
        onFormulaChange,
        valueText,
        currencyCode,
        hasConflictingCurrencies,
        emptyPeriodNames,
        formulaDescription,
    } = useCustomCalculatedField(props);

    if (props.isPreview && isFormulaEditorEnabled) {
        return (
            <FormulaEditor
                fieldName={props.field.customInputFieldName!}
                fieldDescription={props.fieldDescription}
                tokens={tokens}
                formulaFields={props.computedFieldEditing?.formulaFields ?? []}
                onFormulaChange={onFormulaChange}
            />
        );
    }

    if (props.isPreview) {
        return (
            <DotLegalMultiSelect
                label={props.field.customInputFieldName!}
                options={amountFieldOptions}
                chosenOptions={includedAmountFieldIds}
                toolTipText={props.fieldDescription}
                onChange={onIncludedAmountFieldsChange}
                helperText={translateString("fieldsToInclude")}
                noOptionsLabel={translateString("noOptions")}
                errorText={props.errorText}
                isLoading={props.isLoading}
                noMargin
            />
        );
    }

    return (
        <DotLegalTextField
            label={props.field.customInputFieldName!}
            toolTipText={props.fieldDescription}
            value={valueText}
            debounce={false}
            isLoading={props.isLoading}
            errorText={props.errorText}
            helperText={formulaDescription}
            endAdorment={getEndAdornment()}
            disabled
        />
    );

    // The field either has one currency to name or none, so the warning takes the same place in the input the currency code
    // would have had. Letting the input place it keeps this out of the business of measuring where the field lays out its own icons.
    function getEndAdornment() {
        if (hasConflictingCurrencies) {
            return <DotLegalWarningTriangle warningText={translateString("calculatedFieldConflictingCurrenciesWarning")} />;
        }

        if (emptyPeriodNames) {
            return <DotLegalWarningTriangle warningText={translateString("calculatedFieldEmptyPeriodWarning", { names: emptyPeriodNames })} />;
        }

        return currencyCode ? <Box sx={(theme) => ({ color: theme.palette.grey[400], fontSize: 12 })}>{currencyCode}</Box> : undefined;
    }
}

export default CustomCalculatedField;
